import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ImageGenerationService, ImageGenerateResponse } from '../../core/services/image-generation';

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  createdAt: number;
  imageUrl?: string | null;
  pending?: boolean;
}

@Component({
  selector: 'app-image-generation',
  standalone: true,
  imports: [FormsModule, DatePipe, RouterLink],
  templateUrl: './image-generation.html',
  styleUrl: './image-generation.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageGenerationComponent {
  private api = inject(ImageGenerationService);
  private destroyRef = inject(DestroyRef);

  prompt = signal('');
  isGenerating = signal(false);
  errorMsg = signal<string | null>(null);

  messages = signal<ChatMessage[]>([
    {
      id: 'sys-1',
      role: 'assistant',
      text: 'Describe what you want to generate. Add style, lighting, camera, and composition details if you want.',
      createdAt: Date.now(),
    }
  ]);

  images = signal<ImageGenerateResponse[]>([]);
  readonly recentLimit = 6;
  recentImages = computed(() => this.images().slice(0, this.recentLimit));
  isLoadingHistory = signal(false);
  pendingHint = signal('Drawing...');

  canGenerate = computed(() => !this.isGenerating() && this.prompt().trim().length > 0);

  ngOnInit(): void {
    this.loadHistory();
  }

  private loadHistory(): void {
    this.isLoadingHistory.set(true);
    this.api.history(this.recentLimit).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.images.set(list);
        this.isLoadingHistory.set(false);
      },
      error: () => {
        this.isLoadingHistory.set(false);
      },
    });
  }

  private pushMessage(m: ChatMessage): void {
    this.messages.update(list => [...list, m]);
  }

  generate(): void {
    const p = this.prompt().trim();
    if (!p || this.isGenerating()) return;

    this.errorMsg.set(null);
    this.isGenerating.set(true);

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: p,
      createdAt: Date.now(),
    };
    this.pushMessage(userMsg);

    this.prompt.set('');
    const pendingId = crypto.randomUUID();
    this.pendingHint.set('Drawing...');
    this.pushMessage({
      id: pendingId,
      role: 'assistant',
      text: 'Drawing...',
      createdAt: Date.now(),
      pending: true,
    });

    this.api.generate(p).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.messages.update(list => list.filter(m => m.id !== pendingId));
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: 'Done. Want variations? Try adding a style reference or changing the composition.',
          imageUrl: res.image_url,
          createdAt: Date.now(),
        };
        this.pushMessage(assistantMsg);
        this.images.update(list => [res, ...list].slice(0, this.recentLimit));
        this.isGenerating.set(false);
      },
      error: (err) => {
        this.messages.update(list => list.filter(m => m.id !== pendingId));
        this.errorMsg.set(err?.error?.detail || 'Image generation failed.');
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: this.errorMsg() || 'Image generation failed.',
          createdAt: Date.now(),
        };
        this.pushMessage(assistantMsg);
        this.isGenerating.set(false);
      },
    });
  }

}

